/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import moment from 'moment';
import {
  CreatableSelectV2,
  DatePickerV2,
  DefaultSelectV2,
  DialogDefaultV2,
  InputV2,
  UsuarioSelectV2,
} from '../../../components/v2';
import BaseService from 'services/BaseService';
import {
  TIPO_ENTREGA_OPTIONS,
  getRequisicaoItems,
  hasItensPendentes,
  isItemElegivelEntrega,
} from './statusLabels';

const mapTransportadoraOptions = (payload) => {
  const list = Array.isArray(payload) ? payload : [];
  return list
    .map((item) => {
      const label = typeof item === 'string' ? item.trim() : '';
      if (!label) return null;
      return { value: label, label };
    })
    .filter(Boolean);
};

const isDateValid = (value) => {
  if (value === null || value === undefined || value === '') return false;
  return moment(value).isValid();
};

const itemNaEntrega = (item, entrega) =>
  !!item
  && !!entrega
  && (item.entregaId === entrega.id || (entrega.itemIds || []).includes(item.id));

const isSelecionavel = (item, entrega) => {
  if (!item || (item.status !== 'APROVADO' && item.status !== 'PARA_PEDIDO')) return false;
  if (!item.entregaId) return true;
  return itemNaEntrega(item, entrega);
};

const CriarEntregasDialog = ({
  open,
  onClose,
  onConfirm,
  requisicao,
  submitting,
  entrega,
}) => {
  const [tipoEntrega, setTipoEntrega] = useState('TRANSPORTADORA');
  const [transportadoraOption, setTransportadoraOption] = useState(null);
  const [transportadoraOptions, setTransportadoraOptions] = useState([]);
  const [colaboradorId, setColaboradorId] = useState(null);
  const [possuiCodigoRastreamento, setPossuiCodigo] = useState(false);
  const [codigoRastreamento, setCodigoRastreamento] = useState('');
  const [dataEsperadaEntrega, setDataEsperada] = useState(null);
  const [observacao, setObservacao] = useState('');
  const [jaDespachada, setJaDespachada] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState(false);

  const somenteCorrecao = Boolean(entrega && entrega.status === 'ENVIADO');
  const editando = Boolean(entrega);
  const disponiveis = getRequisicaoItems(requisicao).filter((item) =>
    (editando ? isSelecionavel(item, entrega) : isItemElegivelEntrega(item))
  );

  useEffect(() => {
    if (!open) return;
    if (entrega) {
      const temCodigo = Boolean(
        entrega.possuiCodigoRastreamento
        || (entrega.codigoRastreamento && String(entrega.codigoRastreamento).trim())
      );
      const nome = (entrega.transportadora || '').trim();
      setTipoEntrega(entrega.tipoEntrega || 'TRANSPORTADORA');
      setColaboradorId(entrega.colaboradorId || null);
      setPossuiCodigo(temCodigo);
      setCodigoRastreamento(entrega.codigoRastreamento || '');
      setDataEsperada(
        entrega.dataEsperadaEntrega ? moment(entrega.dataEsperadaEntrega).valueOf() : null
      );
      setObservacao(entrega.observacao || '');
      setJaDespachada(false);
      setSelectedIds(
        getRequisicaoItems(requisicao)
          .filter((item) => itemNaEntrega(item, entrega))
          .map((item) => item.id)
      );
      setTransportadoraOption(
        nome || entrega.transportadoraId
          ? { value: entrega.transportadoraId || nome, label: nome || String(entrega.transportadoraId) }
          : null
      );
    } else {
      setTipoEntrega('TRANSPORTADORA');
      setTransportadoraOption(null);
      setColaboradorId(null);
      setPossuiCodigo(false);
      setCodigoRastreamento('');
      setDataEsperada(null);
      setObservacao('');
      setJaDespachada(false);
      setSelectedIds(
        getRequisicaoItems(requisicao).filter(isItemElegivelEntrega).map((item) => item.id)
      );
    }
    setError(false);
    const loadTransportadoras = async () => {
      try {
        const response = await BaseService.get({ url: 'entregas/transportadoras' });
        setTransportadoraOptions(mapTransportadoraOptions(response?.data));
      } catch (e) {
        setTransportadoraOptions([]);
      }
    };
    loadTransportadoras();
  }, [open, entrega]);

  useEffect(() => {
    if (!open || !entrega || somenteCorrecao) return;
    const nome = (entrega.transportadora || '').trim();
    const found = transportadoraOptions.find(
      (option) => option.value === entrega.transportadoraId || option.label === nome
    );
    if (!found) return;
    setTransportadoraOption((current) => {
      if (!current || current.value === found.value || current.label === found.label) return found;
      return current;
    });
  }, [open, entrega, somenteCorrecao, transportadoraOptions]);

  const toggleId = (itemId) => {
    setSelectedIds((current) =>
      current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId]
    );
    setError(false);
  };

  const hasPendentes = !editando && hasItensPendentes(requisicao);
  const bloqueado = somenteCorrecao ? false : (!disponiveis.length || hasPendentes);
  const transportadoraNome = (transportadoraOption && transportadoraOption.label
    ? transportadoraOption.label
    : ''
  ).trim();
  const opcoesTransportadora = (() => {
    if (!transportadoraNome || (transportadoraOption && transportadoraOption.__isNew__)) {
      return transportadoraOptions;
    }
    if (transportadoraOptions.some((option) => option.label === transportadoraNome)) {
      return transportadoraOptions;
    }
    return [{ value: transportadoraNome, label: transportadoraNome }, ...transportadoraOptions];
  })();
  const dataAtual = isDateValid(dataEsperadaEntrega)
    ? moment(dataEsperadaEntrega).format('YYYY-MM-DD')
    : '';
  const dataOriginal = entrega && entrega.dataEsperadaEntrega && moment(entrega.dataEsperadaEntrega).isValid()
    ? moment(entrega.dataEsperadaEntrega).format('YYYY-MM-DD')
    : '';
  const codigoOriginal = entrega && entrega.codigoRastreamento
    ? String(entrega.codigoRastreamento).trim()
    : '';
  const possuiCodigoOriginal = Boolean(
    entrega && (entrega.possuiCodigoRastreamento || codigoOriginal)
  );
  const itensOriginais = entrega
    ? getRequisicaoItems(requisicao)
      .filter((item) => itemNaEntrega(item, entrega))
      .map((item) => item.id)
      .sort()
      .join(',')
    : '';
  const itensAtuais = [...selectedIds].sort().join(',');
  const nomeOriginal = entrega ? String(entrega.transportadora || '').trim() : '';
  const mesmaTransportadora = !entrega || (
    transportadoraOption && transportadoraOption.__isNew__
      ? transportadoraNome === nomeOriginal
      : (
        (entrega.transportadoraId && transportadoraOption && transportadoraOption.value === entrega.transportadoraId)
        || transportadoraNome === nomeOriginal
      )
  );
  const semAlteracao = editando && !jaDespachada && (
    tipoEntrega === (entrega.tipoEntrega || 'TRANSPORTADORA')
    && mesmaTransportadora
    && String(colaboradorId || '') === String(entrega.colaboradorId || '')
    && possuiCodigoRastreamento === possuiCodigoOriginal
    && (possuiCodigoRastreamento ? codigoRastreamento.trim() : '') === (possuiCodigoOriginal ? codigoOriginal : '')
    && dataAtual === dataOriginal
    && (observacao || '') === (entrega.observacao || '')
    && itensAtuais === itensOriginais
  );
  const disabled = bloqueado || semAlteracao;

  const missingTipo = !tipoEntrega;
  const missingItens = !selectedIds.length;
  const exigeDespacho = jaDespachada && !somenteCorrecao;
  const missingData = (exigeDespacho || somenteCorrecao) && !isDateValid(dataEsperadaEntrega);
  const missingTransportadora =
    exigeDespacho && tipoEntrega === 'TRANSPORTADORA' && !transportadoraNome;
  const missingColaborador = tipoEntrega === 'COLABORADOR' && !colaboradorId;
  const missingCodigo =
    possuiCodigoRastreamento && !codigoRastreamento.trim();

  const handleTipoChange = (event) => {
    setTipoEntrega(event.target.value);
    setTransportadoraOption(null);
    setColaboradorId(null);
    setError(false);
  };

  const handleConfirm = () => {
    if (disabled || submitting || semAlteracao) return;
    const invalido = somenteCorrecao
      ? missingData || missingCodigo
      : (
        missingItens
        || missingTipo
        || missingData
        || missingTransportadora
        || missingColaborador
        || missingCodigo
      );
    if (invalido) {
      setError(true);
      return;
    }
    const isNewTransportadora = Boolean(
      transportadoraOption && transportadoraOption.__isNew__
    );
    const rastreio = {
      possuiCodigoRastreamento,
      codigoRastreamento: possuiCodigoRastreamento
        ? codigoRastreamento.trim()
        : null,
    };
    const dataEnviada = (exigeDespacho || somenteCorrecao) && isDateValid(dataEsperadaEntrega)
      ? moment(dataEsperadaEntrega).format('YYYY-MM-DD')
      : (dataOriginal || null);
    const transportadoraEnviada = tipoEntrega !== 'TRANSPORTADORA'
      ? null
      : (exigeDespacho ? transportadoraNome : (nomeOriginal || null));
    const transportadoraIdEnviado = tipoEntrega !== 'TRANSPORTADORA'
      ? null
      : exigeDespacho
        ? (
          !isNewTransportadora
          && transportadoraOption
          && transportadoraOption.value
          && transportadoraOption.value !== transportadoraOption.label
            ? transportadoraOption.value
            : null
        )
        : ((entrega && entrega.transportadoraId) || null);
    const payload = somenteCorrecao
      ? {
        ...rastreio,
        dataEsperadaEntrega: dataEnviada,
      }
      : {
        itemIds: selectedIds,
        tipoEntrega,
        transportadoraId: transportadoraIdEnviado,
        transportadora: transportadoraEnviada || null,
        colaboradorId: tipoEntrega === 'COLABORADOR' ? colaboradorId : null,
        ...rastreio,
        dataEsperadaEntrega: dataEnviada,
        observacao: observacao || null,
        despachada: jaDespachada,
      };
    onConfirm(editando ? payload : { entregas: [payload] });
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={handleConfirm}
      title={somenteCorrecao ? 'Corrigir entrega' : editando ? 'Editar entrega' : 'Criar entrega'}
      confirmMessage={exigeDespacho ? 'Despachar' : editando ? 'Salvar' : 'Criar'}
      confirmLoadingMessage={exigeDespacho ? 'Despachando…' : editando ? 'Salvando…' : 'Criando…'}
      cancelMessage="Fechar"
      maxWidth="sm"
      fullWidth
      useFullScreen={false}
      disabledConfirmButton={disabled}
      confirmLoading={submitting}
    >
      <Grid container spacing={1}>
        {somenteCorrecao && (
          <Grid size={{ xs: 12 }}>
            Depois do despacho só é possível corrigir o rastreio e a data esperada.
          </Grid>
        )}
        {!somenteCorrecao && (
        <>
        <Grid size={{ xs: 12 }}>
          {hasPendentes
            ? 'Decida todos os itens na aba Itens antes de criar uma entrega.'
            : disponiveis.length === 0
              ? 'Não há itens aprovados ou para pedido sem entrega.'
              : disponiveis.map((item) => (
              <FormControlLabel
                key={item.id}
                control={(
                  <Checkbox
                    checked={selectedIds.includes(item.id)}
                    onChange={() => toggleId(item.id)}
                  />
                )}
                label={`${item.nomeItem} (qtd. ${item.quantidade})`}
              />
            ))}
        </Grid>
        <Grid size={{ xs: 12 }}>
          <DefaultSelectV2
            label="Tipo de entrega"
            options={TIPO_ENTREGA_OPTIONS}
            value={tipoEntrega}
            isRequired
            error={error && missingTipo}
            onSelect={handleTipoChange}
          />
        </Grid>
        {tipoEntrega === 'COLABORADOR' && (
          <Grid size={{ xs: 12 }}>
            <UsuarioSelectV2
              label="Colaborador"
              isRequired
              ativo
              clearable
              error={error && missingColaborador}
              value={colaboradorId}
              onSelect={(id) => {
                setColaboradorId(id);
                setError(false);
              }}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={jaDespachada}
                onChange={(event) => {
                  setJaDespachada(event.target.checked);
                  setError(false);
                }}
              />
            )}
            label="Já foi despachada?"
          />
        </Grid>
        {jaDespachada && tipoEntrega === 'TRANSPORTADORA' && (
          <Grid size={{ xs: 12 }}>
            <CreatableSelectV2
              label="Transportadora"
              isRequired
              error={error && missingTransportadora}
              options={opcoesTransportadora}
              value={transportadoraOption}
              onChange={(option) => {
                setTransportadoraOption(option);
                setError(false);
              }}
            />
          </Grid>
        )}
        {jaDespachada && (
          <Grid size={{ xs: 12 }}>
            <DatePickerV2
              label="Data esperada de entrega"
              value={dataEsperadaEntrega}
              isRequired
              error={error && missingData}
              onChange={(value) => {
                setDataEsperada(value);
                setError(false);
              }}
            />
          </Grid>
        )}
        {jaDespachada && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2">
              A entrega entra em trânsito e os itens ficam enviados.
            </Typography>
          </Grid>
        )}
        </>
        )}
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={possuiCodigoRastreamento}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setPossuiCodigo(checked);
                  if (!checked) setCodigoRastreamento('');
                  setError(false);
                }}
              />
            )}
            label="Possui código de rastreamento?"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <InputV2
            label="Código de rastreamento"
            value={codigoRastreamento}
            isRequired={possuiCodigoRastreamento}
            error={error && missingCodigo}
            disabled={!possuiCodigoRastreamento}
            inputProps={{ readOnly: !possuiCodigoRastreamento }}
            onChange={(e) => {
              setCodigoRastreamento(e.target.value);
              setError(false);
            }}
          />
        </Grid>
        {somenteCorrecao && (
          <Grid size={{ xs: 12 }}>
            <DatePickerV2
              label="Data esperada de entrega"
              value={dataEsperadaEntrega}
              isRequired
              error={error && missingData}
              onChange={(value) => {
                setDataEsperada(value);
                setError(false);
              }}
            />
          </Grid>
        )}
        {!somenteCorrecao && (
          <Grid size={{ xs: 12 }}>
            <InputV2
              label="Observação"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              multiline
              rows={2}
            />
          </Grid>
        )}
      </Grid>
    </DialogDefaultV2>
  );
};

CriarEntregasDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  requisicao: PropTypes.object,
  submitting: PropTypes.bool,
  entrega: PropTypes.object,
};

CriarEntregasDialog.defaultProps = {
  requisicao: {},
  submitting: false,
  entrega: null,
};

export default CriarEntregasDialog;
